import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from '@/components/MetricCard';
import { PieChart, ChartContainer } from '@/components/charts';
import { useSmsDeliveryStats, useSmsByCountry, useSmsErrorBreakdown } from '../hooks';
import type { PeriodFilter } from '../api';
import {
  RefreshCw, MessageSquare, Send, CheckCircle, XCircle, Clock, Layers,
  ArrowLeft, TrendingUp
} from 'lucide-react';
import { cn } from '@/lib/utils';

type Period = PeriodFilter;

export default function SmsAnalyticsPage() {
  const { t } = useTranslation('intelligence');
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('week');

  const { data: stats, isLoading: statsLoading, refetch, isFetching } = useSmsDeliveryStats(period);
  const { data: countries, isLoading: countriesLoading } = useSmsByCountry(period);
  const { data: errors, isLoading: errorsLoading } = useSmsErrorBreakdown(period);

  const periods: { value: Period; label: string }[] = [
    { value: 'day', label: t('communications.sms.periods.day', 'Today') },
    { value: 'week', label: t('communications.sms.periods.week', 'This Week') },
    { value: 'month', label: t('communications.sms.periods.month', 'This Month') },
    { value: 'quarter', label: t('communications.sms.periods.quarter', 'This Quarter') },
  ];

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '--';
    return num.toLocaleString();
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value === null) return '--';
    return `${(Math.round(value * 10) / 10)}%`;
  };

  const formatSeconds = (seconds: number | undefined) => {
    if (seconds === undefined || seconds === null) return '--';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const goBack = () => {
    navigate('/intelligence/communications');
  };

  // Error breakdown chart data
  const errorColors = ['#e74c3c', '#f39c12', '#95a5a6', '#3498db', '#9b59b6'];
  const errorChartData = (errors || []).map((e, i) => ({
    name: e.error_code || 'Unknown',
    value: e.count || 0,
    color: errorColors[i % errorColors.length],
  })).filter(d => d.value > 0);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('communications.sms.backToOverview', 'Back to Overview')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-purple-600" />
              {t('communications.sms.title', 'SMS Analytics')}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border bg-white overflow-hidden">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  period === p.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            {t('common:refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          title={t('communications.sms.cards.totalSent', 'Total Sent')}
          value={stats?.total_sent}
          icon={<Send className="h-5 w-5" />}
          color="purple"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.sms.cards.delivered', 'Delivered')}
          value={stats?.delivered}
          icon={<CheckCircle className="h-5 w-5" />}
          color="green"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.sms.cards.failed', 'Failed')}
          value={stats?.failed}
          icon={<XCircle className="h-5 w-5" />}
          color="red"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.sms.cards.segments', 'Segments')}
          value={stats?.total_segments}
          icon={<Layers className="h-5 w-5" />}
          color="blue"
          loading={statsLoading}
        />
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.sms.cards.deliveryRate', 'Delivery Rate')}</p>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-green-600">
                    {formatPercent(stats?.delivery_rate)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.sms.cards.avgDeliveryTime', 'Avg. Delivery Time')}</p>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-blue-600">
                    {formatSeconds(stats?.avg_delivery_time_seconds)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Countries + Error Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Country Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t('communications.sms.sections.countries', 'By Country')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.sms.table.country', 'Country')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.sms.table.sent', 'Sent')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.sms.table.delivered', 'Delivered')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.sms.table.segments', 'Segments')}
                    </th>
                    <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                      {t('communications.sms.table.deliveryRate', 'Delivery Rate')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {countriesLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : countries && countries.length > 0 ? (
                    countries.map((country, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {country.country || country.country_code}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatNumber(country.sent)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatNumber(country.delivered)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {formatNumber(country.segments)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={cn(
                            'font-medium',
                            (country.delivery_rate || 0) >= 95 ? 'text-green-600' :
                            (country.delivery_rate || 0) >= 80 ? 'text-yellow-600' : 'text-red-600'
                          )}>
                            {formatPercent(country.delivery_rate)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                        {t('communications.sms.noCountries', 'No country data available')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Error Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>{t('communications.sms.sections.errors', 'Error Analysis')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={errorsLoading} height={300}>
              {errorChartData.length > 0 ? (
                <PieChart
                  data={errorChartData}
                  height={270}
                  showLegend
                  innerRadius={60}
                />
              ) : (
                <div className="flex items-center justify-center h-[270px] text-gray-400">
                  <div className="text-center">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-400" />
                    <p>{t('communications.sms.noErrors', 'No errors recorded')}</p>
                  </div>
                </div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
